import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, DollarSign, Users, FileText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AdminReports = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [userReport, setUserReport] = useState(null);
  const [revenueReport, setRevenueReport] = useState(null);
  const [contentReport, setContentReport] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchReports();
  }, [user, navigate]);

  const fetchReports = async () => {
    try {
      // Fetch all reports
      const [usersRes, revenueRes, contentRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/reports/users`, {
          headers: { 'Authorization': `Bearer ${user.email}` }
        }),
        fetch(`${BACKEND_URL}/api/admin/reports/revenue`, {
          headers: { 'Authorization': `Bearer ${user.email}` }
        }),
        fetch(`${BACKEND_URL}/api/admin/reports/content`, {
          headers: { 'Authorization': `Bearer ${user.email}` }
        })
      ]);

      if (usersRes.ok) setUserReport(await usersRes.json());
      if (revenueRes.ok) setRevenueReport(await revenueRes.json());
      if (contentRes.ok) setContentReport(await contentRes.json());
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Loading reports...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/admin')} variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
            </div>
            <Button onClick={handleLogout} variant="destructive">
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid gap-6">
          {/* User Growth Report */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Users className="h-6 w-6 text-cyan-600" />
                <CardTitle>User Growth</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium">Total Users</span>
                  <span className="text-2xl font-bold">{userReport?.total_users || 0}</span>
                </div>
                {userReport?.user_growth && Object.keys(userReport.user_growth).length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Recent Signups</h4>
                    <div className="space-y-2">
                      {Object.entries(userReport.user_growth)
                        .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                        .slice(0, 10)
                        .map(([date, count]) => (
                          <div key={date} className="flex justify-between items-center py-2 border-b">
                            <span className="text-sm text-gray-600">{new Date(date).toLocaleDateString()}</span>
                            <span className="font-semibold">{count} users</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Revenue Report */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <DollarSign className="h-6 w-6 text-green-600" />
                <CardTitle>Revenue Analytics</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                  <span className="text-sm font-medium">Total Revenue</span>
                  <span className="text-2xl font-bold text-green-600">
                    ${revenueReport?.total_revenue?.toFixed(2) || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium">Active Subscriptions</span>
                  <span className="text-2xl font-bold">{revenueReport?.total_subscriptions || 0}</span>
                </div>
                
                {revenueReport?.revenue_by_plan && (
                  <div>
                    <h4 className="font-semibold mb-3">Revenue by Plan</h4>
                    <div className="space-y-2">
                      {Object.entries(revenueReport.revenue_by_plan).map(([plan, revenue]) => (
                        <div key={plan} className="flex justify-between items-center py-2 border-b">
                          <span className="text-sm text-gray-600">{plan}</span>
                          <span className="font-semibold">${revenue.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Content Report */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-blue-600" />
                <CardTitle>Content Statistics</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Total Projects</div>
                  <div className="text-3xl font-bold">{contentReport?.projects || 0}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Total Documents</div>
                  <div className="text-3xl font-bold">{contentReport?.documents || 0}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Researcher Profiles</div>
                  <div className="text-3xl font-bold">{contentReport?.researchers || 0}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Total Connections</div>
                  <div className="text-3xl font-bold">{contentReport?.connections || 0}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Platform Health */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <TrendingUp className="h-6 w-6 text-purple-600" />
                <CardTitle>Platform Health</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm">Average Projects per User</span>
                  <span className="font-semibold">
                    {userReport?.total_users > 0 
                      ? (contentReport?.projects / userReport.total_users).toFixed(2)
                      : 0}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm">Average Documents per User</span>
                  <span className="font-semibold">
                    {userReport?.total_users > 0 
                      ? (contentReport?.documents / userReport.total_users).toFixed(2)
                      : 0}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="text-sm">Subscription Rate</span>
                  <span className="font-semibold">
                    {userReport?.total_users > 0 
                      ? ((revenueReport?.total_subscriptions / userReport.total_users) * 100).toFixed(1)
                      : 0}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminReports;
